import { AuthenticatedHttpService } from 'http/httpService'
import { NewServiceApp, ServiceApp, ServiceAppError, ServiceAppErrorCode, ServiceAppWithSecret } from './schema'
import { t } from 'translations'

const camelizeKey = (key: string): string => key.replace(/_([a-z0-9])/g, (_, char: string) => char.toUpperCase())

const restorePermissionSlugs = (
    permissions: Record<string, string[]> | undefined,
    accounts: string[],
): Record<string, string[]> => {
    if (!permissions) {
        return {}
    }
    const camelToSlug = new Map(accounts.map((slug) => [camelizeKey(slug), slug]))
    return Object.entries(permissions).reduce<Record<string, string[]>>((acc, [key, perms]) => {
        acc[camelToSlug.get(key) ?? key] = perms
        return acc
    }, {})
}

const toServiceApp = <T extends ServiceApp>(payload: T): T => ({
    ...payload,
    accounts: payload.accounts ?? [],
    permissions: restorePermissionSlugs(payload.permissions, payload.accounts ?? []),
})

const firstValidationMessage = (errors: unknown): string | null => {
    if (!errors || typeof errors !== 'object') {
        return null
    }
    for (const value of Object.values(errors as Record<string, unknown>)) {
        if (typeof value === 'string') {
            return value
        }
        if (Array.isArray(value) && typeof value[0] === 'string') {
            return value[0]
        }
    }
    return null
}

const asList = (values: unknown): string[] => (Array.isArray(values) ? values.map(String) : [])

const toError = (status: number, body: any): ServiceAppError => {
    const code: ServiceAppErrorCode = body?.error?.code ?? 'unknown'

    if (code === 'scope_exceeds_caller') {
        const blocked = [...asList(body?.error?.forbiddenAccounts), ...asList(body?.error?.forbiddenPermissions)]
        return new ServiceAppError(
            code,
            blocked.length
                ? t('settings.personalSettings.serviceApps.errors.scopeExceedsCallerWithList', {
                      blocked: blocked.join(', '),
                  })
                : t('settings.personalSettings.serviceApps.errors.scopeExceedsCaller'),
        )
    }
    if (code === 'client_quota_exceeded') {
        const limit = body?.error?.limit ?? '25'
        return new ServiceAppError(code, t('settings.personalSettings.serviceApps.errors.quotaExceeded', { limit }))
    }
    if (code === 'validation_error') {
        return new ServiceAppError(
            code,
            firstValidationMessage(body?.error?.errors) ?? t('settings.personalSettings.serviceApps.errors.validation'),
        )
    }
    if (code === 'not_found' || status === 404) {
        return new ServiceAppError('not_found', t('settings.personalSettings.serviceApps.errors.notFound'))
    }
    if (status === 429) {
        return new ServiceAppError('throttled', t('settings.personalSettings.serviceApps.errors.throttled'))
    }
    return new ServiceAppError('unknown', t('settings.personalSettings.serviceApps.errors.unknown'))
}

export class ServiceAppsService {
    constructor(private httpService: AuthenticatedHttpService, private identityEndpoint: string) {}

    private get baseUrl(): string {
        return `${this.identityEndpoint}oauth/service_clients/`
    }

    private async parse(response: Response): Promise<any> {
        const body = await response.json().catch(() => null)
        if (!response.ok) {
            throw toError(response.status, body)
        }
        return body
    }

    async list(): Promise<ServiceApp[]> {
        const response = await this.httpService.fetch(this.baseUrl, { method: 'GET' })
        const body = await this.parse(response)
        return (body?.results ?? []).map(toServiceApp)
    }

    async create(app: NewServiceApp): Promise<ServiceAppWithSecret> {
        const response = await this.httpService.fetch(this.baseUrl, {
            method: 'POST',
            body: JSON.stringify(app),
            headers: { 'Content-Type': 'application/json' },
        })
        return toServiceApp(await this.parse(response))
    }

    async rotateSecret(clientId: string): Promise<ServiceAppWithSecret> {
        const response = await this.httpService.fetch(`${this.baseUrl}${encodeURIComponent(clientId)}/rotate_secret/`, {
            method: 'POST',
        })
        return toServiceApp(await this.parse(response))
    }

    async revoke(clientId: string): Promise<void> {
        const response = await this.httpService.fetch(`${this.baseUrl}${encodeURIComponent(clientId)}/`, {
            method: 'DELETE',
        })
        if (!response.ok) {
            throw toError(response.status, await response.json().catch(() => null))
        }
    }
}
